import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SeasonBreadcrumb from '../../../components/admin/SeasonBreadcrumb';
import { useSeason } from '../../../context/SeasonContext';
import { REVIEW_STATUS, fetchRound, setMatchReviewStatus, syncRoundStats } from '../../../services/calendar';
import { formatDateRange, formatKickoff, formatMoment } from '../../../utils/formatDate';
import { containers, page } from '../../../theme/commonStyles';
import { useNotification } from '../../../context/NotificationContext';

function StatCard({ label, value, sub, color, highlight }) {
    return (
        <Paper
            sx={{
                p: 2,
                flex: 1,
                minWidth: 180,
                ...(highlight ? { borderColor: 'warning.main', borderStyle: 'solid', borderWidth: 1 } : {}),
            }}
        >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{label}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h4" component="div" sx={color ? { color } : undefined}>{value}</Typography>
                <Typography variant="body2" color="text.secondary">{sub}</Typography>
            </Box>
        </Paper>
    );
}

/**
 * De datastatus van een wedstrijd gaat over de API: is er al iets opgehaald, en
 * kan er nog iets veranderen? De controlestatus gaat over de administrator. Twee
 * kolommen naast elkaar, omdat het twee verschillende dingen zijn.
 */
function DataStatus({ match }) {
    if (!match.startingAt || match.startingAt.getTime() > Date.now()) {
        return <Chip size="small" variant="outlined" label="Nog niet gespeeld" />;
    }
    if (!match.statsSyncedAt) {
        return <Chip size="small" variant="outlined" color="warning" label="Niet opgehaald" />;
    }
    if (match.phase === 'finished') {
        return (
            <Box>
                <Chip size="small" variant="outlined" label="Definitief" />
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }} color="text.secondary">
                    {formatMoment(match.statsSyncedAt)}
                </Typography>
            </Box>
        );
    }
    return (
        <Box>
            <Chip size="small" color="success" variant="outlined" label="Live" />
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }} color="text.secondary">
                {formatMoment(match.statsSyncedAt)}
            </Typography>
        </Box>
    );
}

function ReviewStatus({ match }) {
    if (match.reviewStatus === REVIEW_STATUS.APPROVED) {
        return (
            <Box>
                <Chip size="small" color="success" label="Akkoord · vergrendeld" />
                {match.approvedByName && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }} color="text.secondary">
                        {match.approvedByName} · {formatMoment(match.approvedAt)}
                    </Typography>
                )}
            </Box>
        );
    }
    if (!match.startingAt || match.startingAt.getTime() > Date.now()) {
        return <Typography variant="body2" color="text.secondary">—</Typography>;
    }
    if (match.phase !== 'finished') {
        return <Chip size="small" variant="outlined" label="Loopt nog" />;
    }
    return <Chip size="small" variant="outlined" color="warning" label="Te controleren" />;
}

/**
 * Alle wedstrijden van één ronde, met per wedstrijd wat de API zegt en wat de
 * administrator ervan gemaakt heeft. Dit is de plek waar hij ziet wat er nog
 * gecontroleerd moet worden.
 */
function AdminRonde() {
    const { seasonId, roundKey } = useParams();
    const { isLocked } = useSeason();
    const navigate = useNavigate();
    const notify = useNotification();

    const [round, setRound] = useState(null);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [approving, setApproving] = useState(false);
    const [confirmApproveOpen, setConfirmApproveOpen] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await fetchRound(seasonId, roundKey);
            if (!data) setError('Deze ronde staat niet in de kalender van dit seizoen.');
            setRound(data);
        } catch (err) {
            console.error('Kon de ronde niet ophalen:', err);
            setError('De ronde kon niet opgehaald worden.');
        } finally {
            setLoading(false);
        }
    }, [seasonId, roundKey]);

    useEffect(() => { load(); }, [load]);

    const handleSync = async () => {
        setSyncing(true);
        setError('');
        try {
            const result = await syncRoundStats(seasonId, roundKey);
            notify.success(result.message);
            await load();
        } catch (err) {
            console.error('Synchroniseren mislukt:', err);
            setError(err.message || 'De statistieken konden niet opgehaald worden.');
        } finally {
            setSyncing(false);
        }
    };

    // Zet in één keer alle gespeelde, nog niet vergrendelde wedstrijden op akkoord.
    const handleApproveAll = async () => {
        setConfirmApproveOpen(false);
        const targets = (round?.matches || []).filter((match) => match.startingAt
            && match.startingAt.getTime() <= Date.now()
            && match.reviewStatus !== REVIEW_STATUS.APPROVED);
        if (targets.length === 0) return;

        setApproving(true);
        setError('');
        try {
            await Promise.all(targets.map((match) => setMatchReviewStatus(seasonId, roundKey, match.id, true)));
            notify.success(`${targets.length} wedstrijd(en) op akkoord gezet en vergrendeld.`);
            await load();
        } catch (err) {
            console.error('Bulk akkoord mislukt:', err);
            setError(err.message || 'Niet alle wedstrijden konden op akkoord gezet worden.');
        } finally {
            setApproving(false);
        }
    };

    if (loading) {
        return <Box sx={page.loadingContainer}><CircularProgress /></Box>;
    }
    if (!round) {
        return (
            <Box>
                <SeasonBreadcrumb trail={[{ label: 'Kalender & rondes', to: `/admin/season/${seasonId}/kalender` }]} />
                <Alert severity="error">{error || 'Deze ronde bestaat niet.'}</Alert>
            </Box>
        );
    }

    const { progress } = round;
    const toCheck = progress.syncedCount - progress.approvedCount;
    const approveableCount = round.matches.filter((match) => match.startingAt
        && match.startingAt.getTime() <= Date.now()
        && match.reviewStatus !== REVIEW_STATUS.APPROVED).length;

    return (
        <Box>
            <SeasonBreadcrumb
                trail={[{ label: 'Kalender & rondes', to: `/admin/season/${seasonId}/kalender` }]}
                page={round.title}
            />

            <Box sx={{ ...page.header, alignItems: 'flex-end', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h4">{round.title} · puntencontrole</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {[
                            round.subtitle,
                            formatDateRange(round.firstKickoff, round.lastKickoff),
                            `${progress.matchCount} wedstrijden`,
                        ].filter(Boolean).join(' · ')}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        onClick={handleSync}
                        disabled={syncing || approving || isLocked}
                        startIcon={syncing ? <CircularProgress size={18} color="inherit" /> : null}
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        {syncing ? 'Bezig…' : 'Nu synchroniseren'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => setConfirmApproveOpen(true)}
                        disabled={syncing || approving || isLocked || approveableCount === 0}
                        startIcon={approving
                            ? <CircularProgress size={18} color="inherit" />
                            : <CheckCircleIcon />}
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        {approving ? 'Bezig…' : `Alles op akkoord${approveableCount ? ` (${approveableCount})` : ''}`}
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <StatCard
                    label="Akkoord bevonden"
                    value={progress.approvedCount}
                    sub={`van ${progress.startedCount} gespeeld`}
                    color="success.main"
                />
                <StatCard
                    label="Te controleren"
                    value={toCheck > 0 ? toCheck : 0}
                    sub="opgehaald, nog niet nagelopen"
                />
                <StatCard
                    label="Handmatige correcties"
                    value={progress.corrections}
                    sub="blijven staan bij een sync"
                    color="primary.main"
                />
                <StatCard
                    label="Nog niet opgehaald"
                    value={progress.startedCount - progress.syncedCount}
                    sub="gespeeld, zonder cijfers"
                    highlight={progress.startedCount > progress.syncedCount}
                />
            </Box>

            <TableContainer component={Paper} sx={containers.glassContainer}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Wedstrijd</TableCell>
                            <TableCell align="center">Stand</TableCell>
                            <TableCell>Datastatus</TableCell>
                            <TableCell>Controle</TableCell>
                            <TableCell align="right">Correcties</TableCell>
                            <TableCell align="right" sx={{ width: 150 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {round.matches.map((match) => {
                            const played = match.startingAt && match.startingAt.getTime() <= Date.now();
                            const approved = match.reviewStatus === REVIEW_STATUS.APPROVED;
                            const open = () => navigate(
                                `/admin/season/${seasonId}/kalender/${roundKey}/${match.id}`,
                            );

                            return (
                                <TableRow key={match.id} hover onClick={open} sx={{ cursor: 'pointer' }}>
                                    <TableCell>
                                        <Typography variant="body1">{match.title}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatKickoff(match.startingAt)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                        {typeof match.homeGoals === 'number' && typeof match.awayGoals === 'number'
                                            ? `${match.homeGoals}–${match.awayGoals}`
                                            : '–'}
                                    </TableCell>
                                    <TableCell><DataStatus match={match} /></TableCell>
                                    <TableCell><ReviewStatus match={match} /></TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{ fontWeight: match.correctionCount ? 700 : 400, color: match.correctionCount ? 'primary.main' : 'text.secondary' }}
                                    >
                                        {match.correctionCount || '—'}
                                    </TableCell>
                                    <TableCell align="right">
                                        {played && (
                                            <Button
                                                size="small"
                                                variant={approved ? 'text' : 'contained'}
                                                onClick={(event) => { event.stopPropagation(); open(); }}
                                            >
                                                {approved ? 'Bekijken' : 'Controleren'}
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                Een wedstrijd die op akkoord staat wordt door de sync niet meer aangeraakt. Datastatus
                gaat over de API, controle over jou.
            </Typography>

            <Dialog open={confirmApproveOpen} onClose={() => setConfirmApproveOpen(false)}>
                <DialogTitle>Hele ronde op akkoord zetten</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Alle {approveableCount} gespeelde wedstrijd(en) van deze ronde worden op akkoord
                        gezet en vergrendeld. Een vergrendelde wedstrijd wordt door de sync niet meer
                        aangeraakt; je kunt hem later per wedstrijd weer openen.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmApproveOpen(false)}>Annuleren</Button>
                    <Button onClick={handleApproveAll} variant="contained" autoFocus>
                        Akkoord &amp; vergrendelen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default AdminRonde;
